import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { m } from "framer-motion";
import { ArrowRight } from "lucide-react";
import FAQAccordion, { type FAQItem } from "@/components/faq/FAQAccordion";

const FAQSection = () => {
  const { language } = useLanguage();
  const lp = (path: string) => (language === "fr" ? `/fr${path}` : path);

  const content: Record<"fr" | "en", { title: string; viewAll: string; faq: FAQItem[] }> = {
    fr: {
      title: "🧭 FAQ : Tout comprendre sur l'éthique et nos audits",
      viewAll: "Voir toutes les questions",
      faq: [
        {
          question: "Comment savoir si une marque est réellement éthique ou éco-responsable ?",
          intro:
            "C'est tout le problème du marketing actuel : on vous force à comparer des choux et des carottes. Pour obtenir une réponse honnête, il faut sortir du brouillard et utiliser des critères universels. Chez The Wise Compass, nous avons identifié 5 piliers radicaux pour arrêter de deviner et commencer à savoir :",
          pillars: [
            { pillar: "human",     title: "L'Humain",       description: "Aucun achat ne devrait coûter une vie. Nous vérifions les conditions de travail et la sécurité des ouvriers." },
            { pillar: "earth",     title: "L'Écologie",     description: "Au-delà du logo \"vert\", nous analysons les matières premières et l'usage de produits chimiques toxiques." },
            { pillar: "time",      title: "La Durabilité",  description: "Le produit le plus écologique est celui que vous ne rachetez pas. La qualité technique avant tout." },
            { pillar: "territory", title: "La Proximité",   description: "Moins de kérosène dans l'air et plus d'emplois chez nous. Nous favorisons les circuits courts." },
            { pillar: "vision",    title: "La Transparence",description: "Évasion fiscale, profits records, greenwashing... Nous passons au crible ce que les marques préfèrent cacher." },
          ],
          outro: "[link]",
          inlineLink: { label: "Pour en savoir plus, consulte notre méthodologie →", href: lp("/vision") },
        },
        {
          question: "C'est quoi exactement The Wise Compass ?",
          intro:
            "The Wise Compass est bien plus qu'un simple guide d'achat : c'est un levier de transformation pour l'industrie de la mode et de l'outdoor. Nous remplaçons les promesses floues par des audits indépendants et mettons à votre disposition :",
          bullets: [
            "[score] (0-100) : Basés sur des données brutes et vérifiées.",
            "[compare] : Pour trancher entre deux géants de l'équipement.",
            "[top10] : Pour découvrir les pépites éthiques qui remplacent avantageusement les marques conventionnelles.",
          ],
          outro:
            "Parce que chaque achat est un vote, The Wise Compass est votre boussole pour financer aujourd'hui le monde dans lequel vous voulez vivre demain. [vision]",
          inlineLinks: [
            { token: "[score]", label: "Scores d'intégrité", href: lp("/explorer") },
            { token: "[compare]", label: "Comparateur de marques", href: lp("/compare") },
            { token: "[top10]", label: "Tops 10 & Alternatives", href: lp("/top-10") },
            { token: "[vision]", label: "Consulter notre vision →", href: lp("/vision") },
          ],
        },
        {
          question: "Comment comparer deux marques pour savoir laquelle choisir ?",
          intro:
            "Comparer deux marques est souvent un casse-tête : le marketing lisse tout et rend les différences invisibles. Nous avons automatisé ce processus pour vous, en mettant face à face les données brutes sur les critères qui comptent vraiment :",
          bullets: [
            "Performance & Prix : Parce que l'équipement doit d'abord faire son job sur le terrain.",
            "Éthique & Social : Pour vérifier si les conditions de travail sont respectées.",
            "Écologie & Durabilité : Pour analyser l'empreinte carbone et la robustesse des matériaux.",
            "Transparence & Fiscalité : Pour débusquer le greenwashing et l'opacité financière.",
          ],
          outro:
            "Choisissez deux marques (ex: Patagonia vs The North Face), lancez le crash-test, et obtenez un verdict immédiat. [link]",
          inlineLink: { label: "Essaie le comparateur →", href: lp("/compare") },
        },
        {
          question: "Comment sont calculés les scores de 0 à 100 de The Wise Compass ?",
          intro:
            "Pour offrir une vision à 360° de l'intégrité d'une marque, nous avons développé un algorithme propriétaire. Chaque score sur 100 est le résultat d'une analyse croisée de 5 piliers stratégiques et de 15 sous-critères :",
          pillars: [
            { pillar: "human",     title: "L'Humain",       description: "Audit des conditions de travail et de la sécurité des ouvriers." },
            { pillar: "earth",     title: "L'Écologie",     description: "Analyse profonde des matières premières et de l'usage de produits chimiques toxiques." },
            { pillar: "time",      title: "La Durabilité",  description: "Évaluation de la qualité technique. Le produit le plus écologique est celui qu'on ne rachète pas." },
            { pillar: "territory", title: "La Proximité",   description: "Mesure de l'impact logistique. Circuits courts, moins de kérosène, plus d'emplois locaux." },
            { pillar: "vision",    title: "La Transparence",description: "Traque de l'évasion fiscale, des profits opaques et du greenwashing." },
          ],
          outro:
            "D'où viennent les données ? Notre système scanne des milliers de rapports de durabilité, de bases publiques et de registres d'audits, ensuite interprétés par nos IA spécialisées en scores simples et comparables. [link]",
          inlineLink: { label: "Pour en savoir plus, consulte notre méthodologie →", href: lp("/vision") },
        },
        {
          question: "Est-ce que les marques vous paient pour être bien notées ?",
          intro:
            "La réponse est catégorique : Non, jamais. L'indépendance n'est pas une option, c'est notre raison d'être. Pour garantir un verdict 100 % impartial :",
          bullets: [
            "Zéro financement direct : Aucune subvention ni investissement de la part des marques que nous auditons.",
            "Zéro influence : Un score se mérite par des preuves concrètes, jamais par négociation.",
            "Zéro partenariat publicitaire : Nous refusons tout contrat qui pourrait créer un conflit d'intérêts.",
          ],
          outro:
            "Notre boussole pointe vers la vérité, pas vers le plus gros budget marketing.",
        },
        {
          question: "C'est quoi le greenwashing et comment l'éviter ?",
          intro:
            "Le greenwashing est une technique de marketing qui peint en vert une réalité bien plus grise. L'exemple typique : une marque met en avant une collection en \"polyester recyclé\" mais oublie de préciser que ses vêtements sont produits dans des conditions sociales inacceptables, transportés par avion, et que l'entreprise pratique l'évasion fiscale.",
          outro:
            "Pour ne plus tomber dans le panneau, adoptez une vision holistique. Nous croisons 15 critères radicaux (logistique, social, fiscalité, environnement) pour vous donner la vérité nue derrière le score.",
        },
      ],
    },
    en: {
      title: "🧭 FAQ: Understanding Ethics & Our Brand Audits",
      viewAll: "View all questions",
      faq: [
        {
          question: "How can I tell if a brand is truly ethical or sustainable?",
          intro:
            "That's the problem with modern marketing: it forces you to compare apples to oranges. To get an honest answer, you have to cut through the noise and use universal standards. At The Wise Compass, we've identified 5 radical pillars to stop the guessing game and start knowing:",
          pillars: [
            { pillar: "human",     title: "Labor Rights",  description: "No purchase should cost a life. We audit working conditions and worker safety." },
            { pillar: "earth",     title: "Ecology",       description: "We look past the \"green\" logos to analyze raw materials and the use of toxic chemicals." },
            { pillar: "time",      title: "Durability",    description: "The most sustainable product is the one you don't have to replace. Technical quality first." },
            { pillar: "territory", title: "Local Impact",  description: "Less jet fuel, more local jobs. We prioritize short supply chains." },
            { pillar: "vision",    title: "Transparency",  description: "Tax evasion, record shareholder profits, greenwashing... we dig into what brands prefer to hide." },
          ],
          outro: "[link]",
          inlineLink: { label: "To learn more, check our methodology →", href: lp("/vision") },
        },
        {
          question: "What exactly is The Wise Compass?",
          intro:
            "The Wise Compass is more than just a shopping guide; it's a catalyst for change in the fashion and outdoor industries. We replace vague marketing promises with independent audits and provide you with:",
          bullets: [
            "[score] (0-100): Based on raw, verified data.",
            "[compare]: To settle the debate between gear giants.",
            "[top10]: To discover ethical gems that outperform conventional brands.",
          ],
          outro:
            "Because every purchase is a vote, The Wise Compass helps you fund the world you actually want to live in. [vision]",
          inlineLinks: [
            { token: "[score]", label: "Integrity Scores", href: lp("/explorer") },
            { token: "[compare]", label: "Brand Comparison Tool", href: lp("/compare") },
            { token: "[top10]", label: "Top 10s & Alternatives", href: lp("/top-10") },
            { token: "[vision]", label: "Read our vision →", href: lp("/vision") },
          ],
        },
        {
          question: "How do I compare two brands to decide which one to buy?",
          intro:
            "Comparing brands is usually a headache because marketing makes the real differences invisible. We've automated this for you, going head-to-head on the criteria that actually matter:",
          bullets: [
            "Performance & Price: Because gear has to perform in the field first.",
            "Ethics & Social Impact: To verify if labor rights are respected.",
            "Ecology & Sustainability: To analyze carbon footprints and material durability.",
            "Transparency & Tax Integrity: To expose greenwashing and financial opacity.",
          ],
          outro:
            "Choose two brands (e.g., Patagonia vs. The North Face), run the crash-test, and get an instant verdict. [link]",
          inlineLink: { label: "Try the comparison tool →", href: lp("/compare") },
        },
        {
          question: "How are The Wise Compass scores (0-100) calculated?",
          intro:
            "To provide a 360° view of a brand's integrity, we developed a proprietary algorithm. Each score is the result of a cross-analysis of 5 strategic pillars and 15 sub-criteria:",
          pillars: [
            { pillar: "human",     title: "Human",        description: "Labor audits and factory safety." },
            { pillar: "earth",     title: "Ecology",      description: "Deep-dive into raw materials and toxicity." },
            { pillar: "time",      title: "Durability",   description: "Evaluation of technical build and lifespan." },
            { pillar: "territory", title: "Supply Chain", description: "Measuring logistics impact and local economic contribution." },
            { pillar: "vision",    title: "Transparency", description: "Tracking tax integrity and corporate honesty." },
          ],
          outro:
            "Where does the data come from? Our system scans thousands of sustainability reports, public databases and third-party audit registries, then turns them into simple, comparable scores via our specialized AI. [link]",
          inlineLink: { label: "To learn more, check our methodology →", href: lp("/vision") },
        },
        {
          question: "Do brands pay you to get better ratings?",
          intro:
            "The answer is a hard No. Never. Independence isn't an option; it's our core identity. To ensure a 100% unbiased verdict:",
          bullets: [
            "Zero Direct Funding: No grants, donations or investments from the brands we audit.",
            "Zero Influence: A score is earned, not bought. No brand can negotiate its rating.",
            "Zero Ad Partnerships: We refuse any contract that could create a conflict of interest.",
          ],
          outro:
            "Our compass points toward the truth, not the biggest marketing budget.",
        },
        {
          question: "What is greenwashing and how can I avoid it?",
          intro:
            "Greenwashing is a marketing tactic used to paint a \"green\" image over a much grayer reality. The classic example: a brand promotes a \"recycled polyester\" collection but \"forgets\" to mention that the clothes are made in sweatshops, shipped by air, and that the company avoids local taxes.",
          outro:
            "To stop falling for it, adopt a holistic view. We cross 15 radical criteria (logistics, social, tax, environment) to give you the naked truth behind the score.",
        },
      ],
    },
  };

  const t = content[language];

  return (
    <section className="py-20 px-6 bg-background overflow-hidden">
      <div className="container mx-auto max-w-3xl">
        <m.h2
          className="font-serif text-3xl md:text-4xl text-foreground text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6 }}
        >
          {t.title}
        </m.h2>

        <m.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <FAQAccordion items={t.faq} />
        </m.div>

        <m.div
          className="text-center mt-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
          >
            {t.viewAll}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </m.div>
      </div>
    </section>
  );
};

export default FAQSection;
